import React, { useState, useEffect } from 'react';
import { 
  Steps, 
  Card, 
  Form, 
  Input, 
  Select, 
  Radio, 
  Switch, 
  Button, 
  Space, 
  Alert, 
  Divider,
  Row,
  Col,
  Typography,
  message,
  Tag,
  Tooltip,
  InputNumber
} from 'antd';
import { 
  DatabaseOutlined, 
  SettingOutlined, 
  SafetyCertificateOutlined, 
  PlayCircleOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { ConnectorJob, ConnectorType, ProcessingMode, MetadataStorage } from '../types/connector';
import { connectorApi } from '../services/connectorApi';

const { Step } = Steps;
const { Option } = Select;
const { TextArea } = Input;
const { Text, Title } = Typography;

interface ConnectorWizardProps {
  mode: 'create' | 'edit';
  initialConfig?: Partial<ConnectorJob>;
  onSave: (config: ConnectorJob) => Promise<void>;
  onCancel: () => void;
}

interface WizardState {
  current: number;
  connectorType: ConnectorType | null;
  configuration: Partial<ConnectorJob>;
  testResult: any;
  capabilities: any[];
}

const ConnectorWizard: React.FC<ConnectorWizardProps> = ({
  mode,
  initialConfig,
  onSave,
  onCancel
}) => {
  const [form] = Form.useForm();
  const [state, setState] = useState<WizardState>({
    current: 0,
    connectorType: initialConfig?.connector_type || null,
    configuration: initialConfig || {},
    testResult: null,
    capabilities: []
  });
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);

  // Load capabilities on mount
  useEffect(() => {
    loadCapabilities();
  }, []);

  const loadCapabilities = async () => {
    try {
      const capabilities = await connectorApi.getCapabilities();
      console.log('Loaded capabilities:', capabilities);
      setState(prev => ({ ...prev, capabilities: capabilities || [] }));
    } catch (error) {
      console.error('Failed to load capabilities:', error);
      message.error('Failed to load capabilities. Using defaults.');
      // Use default capabilities as fallback
      const defaultCapabilities = [
        {
          name: "defense_classification",
          display_name: "Defense Classification",
          description: "Classify defense documents according to SCG rules",
          domain: "defense",
          supported_file_types: ["pdf", "docx", "txt"],
          estimated_processing_time: 2.5
        },
        {
          name: "legal_privilege",
          display_name: "Legal Privilege Analysis", 
          description: "Identify attorney-client privilege and work product doctrine",
          domain: "legal",
          supported_file_types: ["pdf", "docx", "txt", "eml"],
          estimated_processing_time: 1.8
        },
        {
          name: "healthcare_phi",
          display_name: "Healthcare PHI Detection",
          description: "Detect protected health information for HIPAA compliance", 
          domain: "healthcare",
          supported_file_types: ["pdf", "docx", "txt", "xml", "hl7"],
          estimated_processing_time: 1.2
        }
      ];
      setState(prev => ({ ...prev, capabilities: defaultCapabilities }));
    }
  };

  const steps = [
    {
      title: 'Connector Type',
      icon: <DatabaseOutlined />,
      description: 'Choose data source type'
    },
    {
      title: 'Authentication',
      icon: <SafetyCertificateOutlined />,
      description: 'Configure access credentials'
    },
    {
      title: 'Source Configuration',
      icon: <SettingOutlined />,
      description: 'Specify data source settings'
    },
    {
      title: 'Processing Options',
      icon: <PlayCircleOutlined />,
      description: 'Configure analysis settings'
    },
    {
      title: 'Review & Test',
      icon: <CheckCircleOutlined />,
      description: 'Validate and save configuration'
    }
  ];

  const next = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue();
      
      setState(prev => ({
        ...prev,
        current: prev.current + 1,
        configuration: { ...prev.configuration, ...values }
      }));
    } catch (error) {
      console.log('Validation failed:', error);
    }
  };

  const prev = () => {
    setState(prev => ({ ...prev, current: prev.current - 1 }));
  };

  const handleConnectorTypeChange = (type: ConnectorType) => {
    setState(prev => ({ 
      ...prev, 
      connectorType: type,
      configuration: { ...prev.configuration, connector_type: type }
    }));
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const values = form.getFieldsValue();
      const config = { ...state.configuration, ...values };
      
      // Create temporary job for testing
      const _testJob = {
        id: 'test',
        name: 'Connection Test',
        user_id: 'current_user',
        ...config
      };
      
      // This would call a test endpoint - simplified for demo
      setState(prev => ({
        ...prev,
        testResult: {
          success: true,
          message: 'Connection successful',
          details: { bucket_accessible: true, credentials_valid: true }
        }
      }));
      
      message.success('Connection test successful!');
    } catch (error) {
      setState(prev => ({
        ...prev,
        testResult: {
          success: false,
          message: (error as Error).message || 'Connection failed',
          details: {}
        }
      }));
      message.error('Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleFinish = async () => {
    try {
      setLoading(true);
      const values = form.getFieldsValue();
      const finalConfig = { ...state.configuration, ...values };
      
      console.log('Creating connector with config:', finalConfig);
      await onSave(finalConfig as ConnectorJob);
      message.success(mode === 'create' ? 'Connector created successfully!' : 'Connector updated successfully!');
    } catch (error) {
      console.error('Failed to create connector:', error);
      message.error(`Failed to ${mode} connector: ${(error as Error).message}`);
    } finally {
      setLoading(false);
    }
  };

  // Step 1: Connector Type Selection
  const renderConnectorTypeStep = () => (
    <div>
      <Title level={4}>Select Data Source Type</Title>
      <Text type="secondary">
        Choose the type of data source you want to connect to Cognitive Fabric.
      </Text>
      
      <Form form={form} layout="vertical" style={{ marginTop: 24 }}>
        <Row gutter={24}>
          <Col span={12}>
            <Card 
              hoverable
              onClick={() => handleConnectorTypeChange('s3')}
              style={{ 
                border: state.connectorType === 's3' ? '2px solid #1890ff' : '1px solid #d9d9d9',
                cursor: 'pointer'
              }}
            >
              <div style={{ textAlign: 'center', padding: 20 }}>
                <DatabaseOutlined style={{ fontSize: 48, color: '#ff9500' }} />
                <Title level={4} style={{ marginTop: 16, marginBottom: 8 }}>
                  Amazon S3
                </Title>
                <Text type="secondary">
                  Connect to AWS S3 buckets for document processing
                </Text>
                <div style={{ marginTop: 16 }}>
                  <Tag color="blue">Cloud Storage</Tag>
                  <Tag color="green">High Volume</Tag>
                </div>
              </div>
            </Card>
          </Col>
          
          <Col span={12}>
            <Card 
              hoverable
              onClick={() => handleConnectorTypeChange('mongodb')}
              style={{ 
                border: state.connectorType === 'mongodb' ? '2px solid #1890ff' : '1px solid #d9d9d9',
                cursor: 'pointer'
              }}
            >
              <div style={{ textAlign: 'center', padding: 20 }}>
                <DatabaseOutlined style={{ fontSize: 48, color: '#13aa52' }} />
                <Title level={4} style={{ marginTop: 16, marginBottom: 8 }}>
                  MongoDB
                </Title>
                <Text type="secondary">
                  Connect to MongoDB collections for document analysis
                </Text>
                <div style={{ marginTop: 16 }}>
                  <Tag color="green">NoSQL</Tag>
                  <Tag color="blue">Real-time</Tag>
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      </Form>
    </div>
  );

  // Step 2: Authentication Configuration
  const renderAuthStep = () => (
    <div>
      <Title level={4}>Authentication Configuration</Title>
      <Text type="secondary">
        Configure authentication credentials for accessing your data source.
      </Text>
      
      <Form form={form} layout="vertical" style={{ marginTop: 24 }}>
        {state.connectorType === 's3' && (
          <>
            <Form.Item
              name={['auth_config', 'method']}
              label="Authentication Method"
              rules={[{ required: true }]}
            >
              <Radio.Group>
                <Radio value="iam_access_keys">IAM Access Keys</Radio>
                <Radio value="iam_role">IAM Role</Radio>
              </Radio.Group>
            </Form.Item>
            
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue }: { getFieldValue: (name: string | string[]) => any }) => {
                const authMethod = getFieldValue(['auth_config', 'method']);
                
                if (authMethod === 'iam_access_keys') {
                  return (
                    <>
                      <Form.Item
                        name={['auth_config', 'access_key_id']}
                        label="Access Key ID"
                        rules={[{ required: true }]}
                      >
                        <Input placeholder="AKIA..." />
                      </Form.Item>
                      <Form.Item
                        name={['auth_config', 'secret_access_key']}
                        label="Secret Access Key"
                        rules={[{ required: true }]}
                      >
                        <Input.Password placeholder="Secret access key" />
                      </Form.Item>
                    </>
                  );
                } else if (authMethod === 'iam_role') {
                  return (
                    <>
                      <Form.Item
                        name={['auth_config', 'role_arn']}
                        label="Role ARN"
                        rules={[{ required: true }]}
                      >
                        <Input placeholder="arn:aws:iam::123456789012:role/RoleName" />
                      </Form.Item>
                      <Form.Item
                        name={['auth_config', 'external_id']}
                        label="External ID (Optional)"
                      >
                        <Input placeholder="External ID for role assumption" />
                      </Form.Item>
                    </>
                  );
                }
                return null;
              }}
            </Form.Item>
            
            <Form.Item
              name={['auth_config', 'region']}
              label="AWS Region"
              rules={[{ required: true }]}
              initialValue="us-east-1"
            >
              <Select>
                <Option value="us-east-1">US East (N. Virginia)</Option>
                <Option value="us-west-2">US West (Oregon)</Option>
                <Option value="eu-west-1">Europe (Ireland)</Option>
                <Option value="ap-southeast-1">Asia Pacific (Singapore)</Option>
              </Select>
            </Form.Item>
          </>
        )}
        
        {state.connectorType === 'mongodb' && (
          <>
            <Form.Item
              name={['auth_config', 'method']}
              label="Authentication Method"
              rules={[{ required: true }]}
            >
              <Radio.Group>
                <Radio value="username_password">Username/Password</Radio>
                <Radio value="connection_string">Connection String</Radio>
              </Radio.Group>
            </Form.Item>
            
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue }: { getFieldValue: (name: string | string[]) => any }) => {
                const authMethod = getFieldValue(['auth_config', 'method']);
                
                if (authMethod === 'username_password') {
                  return (
                    <>
                      <Form.Item
                        name={['auth_config', 'username']}
                        label="Username"
                        rules={[{ required: true }]}
                      >
                        <Input placeholder="MongoDB username" />
                      </Form.Item>
                      <Form.Item
                        name={['auth_config', 'password']}
                        label="Password"
                        rules={[{ required: true }]}
                      >
                        <Input.Password placeholder="MongoDB password" />
                      </Form.Item>
                      <Form.Item
                        name={['auth_config', 'auth_database']}
                        label="Authentication Database"
                        initialValue="admin"
                      >
                        <Input placeholder="admin" />
                      </Form.Item>
                    </>
                  );
                } else if (authMethod === 'connection_string') {
                  return (
                    <Form.Item
                      name={['auth_config', 'connection_string']}
                      label="Connection String"
                      rules={[{ required: true }]}
                    >
                      <Input placeholder="mongodb://username:password@host:port/database" />
                    </Form.Item>
                  );
                }
                return null;
              }}
            </Form.Item>
          </>
        )}
      </Form>
    </div>
  );

  // Step 3: Source Configuration
  const renderSourceConfigStep = () => (
    <div>
      <Title level={4}>Source Configuration</Title>
      <Text type="secondary">
        Specify the location and filtering options for your data source.
      </Text>
      
      <Form form={form} layout="vertical" style={{ marginTop: 24 }}>
        {state.connectorType === 's3' && (
          <>
            <Form.Item
              name={['source_config', 'bucket_name']}
              label="Bucket Name"
              rules={[{ required: true }]}
            >
              <Input placeholder="my-documents-bucket" />
            </Form.Item>
            <Form.Item
              name={['source_config', 'prefix']}
              label="Prefix (Optional)"
            >
              <Input placeholder="documents/incoming/" />
            </Form.Item>
          </>
        )}
        
        {state.connectorType === 'mongodb' && (
          <>
            <Form.Item
              name={['source_config', 'database']}
              label="Database Name"
              rules={[{ required: true }]}
            >
              <Input placeholder="documents_db" />
            </Form.Item>
            <Form.Item
              name={['source_config', 'collection']}
              label="Collection Name"
              rules={[{ required: true }]}
            >
              <Input placeholder="documents" />
            </Form.Item>
          </>
        )}
        
        <Form.Item
          name="file_filters"
          label="File Filters"
        >
          <Select
            mode="tags"
            placeholder="e.g., *.pdf, *.docx"
            tokenSeparators={[',']}
            style={{ width: '100%' }}
            dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
          >
            <Option value="*.pdf">PDF Files</Option>
            <Option value="*.docx">Word Documents</Option>
            <Option value="*.txt">Text Files</Option>
            <Option value="*.eml">Email Files</Option>
          </Select>
        </Form.Item>
        
        <Form.Item
          name="max_documents"
          label="Maximum Documents (Optional)"
        >
          <InputNumber
            style={{ width: '100%' }}
            placeholder="Leave empty for no limit"
            min={1}
          />
        </Form.Item>
      </Form>
    </div>
  );

  // Step 4: Processing Options
  const renderProcessingStep = () => (
    <div>
      <Title level={4}>Processing Configuration</Title>
      <Text type="secondary">
        Configure how documents will be analyzed and processed.
      </Text>
      
      <Form form={form} layout="vertical" style={{ marginTop: 24 }}>
        <Form.Item
          name="name"
          label="Connector Name"
          rules={[{ required: true }]}
        >
          <Input placeholder="My Document Classifier" />
        </Form.Item>
        
        <Form.Item
          name="description"
          label="Description (Optional)"
        >
          <TextArea rows={3} placeholder="Describe this connector's purpose..." />
        </Form.Item>
        
        <Form.Item
          name="capability"
          label="Processing Capability"
          rules={[{ required: true }]}
        >
          <Select 
            placeholder="Select analysis capability"
            style={{ width: '100%' }}
            dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
          >
            {state.capabilities.map((cap) => (
              <Option key={cap.name} value={cap.name}>
                <div>
                  <Text strong>{cap.display_name}</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {cap.description}
                  </Text>
                </div>
              </Option>
            ))}
          </Select>
        </Form.Item>
        
        <Form.Item
          name="processing_mode"
          label="Processing Mode"
          rules={[{ required: true }]}
          initialValue={ProcessingMode.BATCH}
        >
          <Radio.Group>
            <Radio value={ProcessingMode.BATCH}>
              <div>
                <div>Batch Processing</div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Process all documents in batches (recommended for large datasets)
                </Text>
              </div>
            </Radio>
            <Radio value={ProcessingMode.REALTIME}>
              <div>
                <div>Real-time Processing</div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Process documents as they are added to the source
                </Text>
              </div>
            </Radio>
          </Radio.Group>
        </Form.Item>
        
        <Form.Item
          name="batch_size"
          label="Batch Size"
          initialValue={10}
        >
          <InputNumber min={1} max={100} style={{ width: '100%' }} />
        </Form.Item>
        
        <Form.Item
          name="metadata_storage"
          label="Metadata Storage"
          rules={[{ required: true }]}
          initialValue={MetadataStorage.SOURCE_TAGS}
        >
          <Select 
            style={{ width: '100%' }}
            dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
          >
            <Option value={MetadataStorage.SOURCE_TAGS}>
              <div>
                <div>Source Tags</div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Store results as tags/metadata in the source system
                </Text>
              </div>
            </Option>
            <Option value={MetadataStorage.SIDECAR_FILES}>
              <div>
                <div>Sidecar Files</div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Create separate metadata files alongside documents
                </Text>
              </div>
            </Option>
          </Select>
        </Form.Item>
        
        <Form.Item
          name="schedule"
          label="Schedule (Optional)"
        >
          <Input placeholder="0 2 * * * (cron expression for daily at 2 AM)" />
        </Form.Item>
        
        <Form.Item
          name="enabled"
          label="Enable Connector"
          valuePropName="checked"
          initialValue={true}
        >
          <Switch />
        </Form.Item>
      </Form>
    </div>
  );

  // Step 5: Review & Test
  const renderReviewStep = () => (
    <div>
      <Title level={4}>Review Configuration</Title>
      <Text type="secondary">
        Review your connector configuration and test the connection.
      </Text>
      
      <div style={{ marginTop: 24 }}>
        <Card title="Configuration Summary" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <div><Text strong>Connector Type:</Text> {state.connectorType?.toUpperCase()}</div>
              <div><Text strong>Name:</Text> {state.configuration.name}</div>
              <div><Text strong>Capability:</Text> {state.configuration.capability}</div>
            </Col>
            <Col span={12}>
              <div><Text strong>Processing Mode:</Text> {state.configuration.processing_mode}</div>
              <div><Text strong>Batch Size:</Text> {state.configuration.batch_size}</div>
              <div><Text strong>Enabled:</Text> {state.configuration.enabled ? 'Yes' : 'No'}</div>
            </Col>
          </Row>
        </Card>
        
        <Card title="Connection Test" style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 16 }}>
            <Button
              type="primary"
              loading={testing}
              onClick={testConnection}
              icon={<PlayCircleOutlined />}
            >
              Test Connection
            </Button>
          </div>
          
          {state.testResult && (
            <Alert
              message={state.testResult.success ? 'Connection Successful' : 'Connection Failed'}
              description={state.testResult.message}
              type={state.testResult.success ? 'success' : 'error'}
              showIcon
            />
          )}
        </Card>
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch (state.current) {
      case 0: return renderConnectorTypeStep();
      case 1: return renderAuthStep();
      case 2: return renderSourceConfigStep();
      case 3: return renderProcessingStep();
      case 4: return renderReviewStep();
      default: return null;
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Steps current={state.current} style={{ marginBottom: 32 }}>
          {steps.map(step => (
            <Step
              key={step.title}
              title={step.title}
              description={step.description}
              icon={step.icon}
            />
          ))}
        </Steps>
        
        <div style={{ minHeight: 400 }}>
          {renderCurrentStep()}
        </div>
        
        <Divider />
        
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Space>
            <Button onClick={onCancel}>
              Cancel
            </Button>
            {state.current > 0 && (
              <Button onClick={prev}>
                Previous
              </Button>
            )}
          </Space>
          
          <Space>
            {state.current < steps.length - 1 && (
              <Button 
                type="primary" 
                onClick={next}
                disabled={!state.connectorType && state.current === 0}
              >
                Next
              </Button>
            )}
            {state.current === steps.length - 1 && (
              <Button 
                type="primary" 
                loading={loading}
                onClick={handleFinish}
              >
                {mode === 'create' ? 'Create Connector' : 'Update Connector'}
              </Button>
            )}
          </Space>
        </div>
      </Card>
    </div>
  );
};

export default ConnectorWizard;